<template>
  <div class="ai-err">
    <div class="ai-err__head">
      <span
        class="ai-err__dot"
        aria-hidden="true"
      />
      <span class="ai-err__title">Something broke</span>
    </div>

    <p class="ai-err__raw">
      {{ error || 'unknown error' }}
    </p>

    <p class="ai-err__reading">
      {{ reading }}
      <span class="ai-err__next">{{ next }}</span>
    </p>

    <div class="ai-err__foot">
      <span
        class="ai-err__wire"
        aria-hidden="true"
      >
        <span class="ai-err__end ai-err__end--me" />
        <span class="ai-err__cable ai-err__cable--near" />
        <span class="ai-err__cable ai-err__cable--far" />
        <span class="ai-err__packet" />
        <span class="ai-err__ping" />
        <span class="ai-err__end ai-err__end--them" />
      </span>
      <p class="ai-err__quip">
        {{ quip }}
      </p>
    </div>

    <p class="ai-err__nag">
      {{ prompt.before }}
      <a
        class="ai-err__mail"
        :href="mailto"
      >{{ prompt.label }}</a>
      {{ prompt.after }}
      <button
        class="ai-err__copy"
        type="button"
        @click="copyReport"
      >
        Copy full report
      </button>
    </p>
  </div>
</template>

<script>
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { SUPPORT_PROMPT, pickQuip, buildMailto, buildReport, explainError, nextStep } from './error-copy'

export default {
  name: 'AiAgentErrorCard',

  mixins: [copyToClipboardMixin],

  props: {
    error: {
      type: String,
      default: ''
    },
    seed: {
      type: String,
      default: ''
    },
    at: {
      type: Number,
      default: 0
    },
    // Everything the report needs that the card cannot see for itself: the
    // prompt, the tool calls, whatever streamed back, the endpoint and session.
    diagnostics: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return { prompt: SUPPORT_PROMPT }
  },

  computed: {
    quip() {
      return pickQuip(this.seed)
    },
    reading() {
      return explainError(this.error)
    },
    next() {
      return nextStep(this.error)
    },
    // The card owns the error and the timestamp; the parent supplies the rest.
    report() {
      return { ...this.diagnostics, error: this.error, at: this.at }
    },
    mailto() {
      return buildMailto(this.report)
    }
  },

  methods: {
    copyReport() {
      this.copyTextToClipboard(buildReport(this.report), 'Failure report copied. Paste it into the email.')
    }
  }
}
</script>

<style lang="scss" scoped>
$ai-mono-font: 'SF Mono', 'JetBrains Mono', 'Menlo', 'Consolas', monospace;
$ai-ui-font: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

.ai-err {
  flex: 1;
  min-width: 0;
  padding: 11px 14px 12px;
  background: rgba(248, 113, 113, 0.06);
  border-left: 2px solid rgba(248, 113, 113, 0.55);
  border-radius: 0 6px 6px 0;
  font-family: $ai-ui-font;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__dot {
    width: 7px;
    height: 7px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #f87171;
    animation: ai-err-pulse 2s ease-in-out infinite;
  }

  &__title {
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.2px;
  }

  // The raw error, visible without a click: it is the first thing an admin
  // needs and the thing they paste into a bug report.
  &__raw {
    margin: 8px 0 0;
    padding: 7px 10px;
    background: rgba(0, 0, 0, 0.28);
    border-radius: 4px;
    color: #fca5a5;
    font-family: $ai-mono-font;
    font-size: 0.74rem;
    line-height: 1.45;
    word-break: break-word;
  }

  &__reading {
    margin: 8px 0 0;
    color: #e2d3d3;
    font-size: 0.82rem;
    line-height: 1.55;
  }

  // Brighter than the reading it follows: what to do next is the part an admin
  // acts on, and half the time it is "retry", not "write in".
  &__next {
    color: #ffffff;
    font-weight: 500;
  }

  &__foot {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    margin-top: 11px;
    padding-top: 10px;
    border-top: 1px solid rgba(248, 113, 113, 0.2);
  }

  // The request leaves, hits the break in the middle, and never arrives. All
  // five pieces share one 2.8s cycle so the impact, the ping and the far end
  // flickering all land on the same beat.
  &__wire {
    position: relative;
    flex-shrink: 0;
    width: 60px;
    height: 14px;
    margin-top: 3px;
  }

  &__end {
    position: absolute;
    top: 4px;
    width: 6px;
    height: 6px;
    border-radius: 50%;

    &--me {
      left: 0;
      background: #f5a5b8;
      animation: ai-err-send 2.8s ease-in-out infinite;
    }

    // Dim, because nothing is home. It stirs once as the packet hits, then
    // gives up again.
    &--them {
      right: 0;
      background: #8d7f86;
      animation: ai-err-dead 2.8s ease-in-out infinite;
    }
  }

  &__cable {
    position: absolute;
    top: 6px;
    height: 1px;
    width: 17px;

    &--near {
      left: 9px;
      background: rgba(245, 165, 184, 0.45);
    }

    // Dashes past the break: the half of the route that is not carrying anything.
    &--far {
      right: 9px;
      background: repeating-linear-gradient(
        to right,
        rgba(141, 127, 134, 0.6) 0 2px,
        transparent 2px 5px
      );
    }
  }

  &__packet {
    position: absolute;
    top: 4.5px;
    left: 9px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.7);
    animation: ai-err-packet 2.8s ease-in infinite;
  }

  // Expanding ring at the break, timed to the moment the packet dies.
  &__ping {
    position: absolute;
    top: 2px;
    left: 25px;
    width: 10px;
    height: 10px;
    border: 1px solid #f87171;
    border-radius: 50%;
    opacity: 0;
    animation: ai-err-ping 2.8s ease-out infinite;
  }

  &__quip {
    margin: 0;
    color: #b9a3ad;
    font-size: 0.78rem;
    line-height: 1.55;
    font-style: italic;
  }

  // The quietest line on the card. Reaching for the mail link should feel like
  // a last resort, so it is sized and coloured below everything above it.
  &__nag {
    margin: 9px 0 0;
    color: #8d7f86;
    font-size: 0.72rem;
    line-height: 1.5;
  }

  // The address itself never renders. It lives in the href, so it only shows up
  // in the browser's status bar on hover.
  &__mail {
    color: #c98da0;
    border-bottom: 1px dotted rgba(201, 141, 160, 0.45);

    &:hover {
      color: #ffffff;
      border-bottom-color: #ffffff;
      text-decoration: none;
    }
  }

  // Styled as a link, not a button: it sits inside a sentence and a real button
  // chrome would outweigh the mail link next to it.
  &__copy {
    margin-left: 6px;
    padding: 0;
    background: none;
    border: 0;
    border-bottom: 1px dotted rgba(141, 127, 134, 0.5);
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:hover {
      color: #ffffff;
      border-bottom-color: #ffffff;
    }
  }
}

@keyframes ai-err-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.5); }
  70%      { opacity: 0.55; box-shadow: 0 0 0 5px rgba(248, 113, 113, 0); }
}

// Fires as the packet leaves, so the near end reads as the one still trying.
@keyframes ai-err-send {
  0%, 100% { transform: scale(1);   opacity: 0.75; }
  8%       { transform: scale(1.3); opacity: 1; }
  40%      { transform: scale(1);   opacity: 0.75; }
}

@keyframes ai-err-packet {
  0%       { transform: translateX(0) scale(1);   opacity: 0; }
  8%       { transform: translateX(1px) scale(1); opacity: 1; }
  42%      { transform: translateX(19px) scale(1); opacity: 1; }
  48%      { transform: translateX(20px) scale(1.5); opacity: 1; }
  56%      { transform: translateX(16px) scale(0.8); opacity: 0.45; }
  66%      { transform: translateX(15px) scale(0.5); opacity: 0; }
  100%     { transform: translateX(0) scale(1);   opacity: 0; }
}

@keyframes ai-err-ping {
  0%, 43%   { transform: scale(0.2); opacity: 0; }
  50%       { transform: scale(0.5); opacity: 0.85; }
  74%, 100% { transform: scale(1.8); opacity: 0; }
}

@keyframes ai-err-dead {
  0%, 44%   { opacity: 0.25; }
  50%       { opacity: 0.65; }
  58%, 100% { opacity: 0.18; }
}

// Respect the OS setting. The broken cable still reads as broken when it is
// frozen, so only the moving parts go away.
@media (prefers-reduced-motion: reduce) {
  .ai-err__dot,
  .ai-err__end,
  .ai-err__ping {
    animation: none;
  }

  .ai-err__packet {
    animation: none;
    transform: translateX(14px);
    opacity: 0.5;
  }
}
</style>
